import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Tooltip } from '@speeki/global-ui-components';

import { HeaderLogos } from '@components/molecules/HeaderLogos/HeaderLogos';
import { LanguageSelect } from '@components/molecules/LanguageSelect/LanguageSelect';
import { LoginText } from '@components/molecules/LoginText/LoginText';
import { Divider } from '@components/organisms/DesktopMenu/DesktopMenu.styled';

import { useResponsive } from '@utils/hooks/useResponsive';

import { getPath } from '@services/RouteService/paths';

import {
    CurrentSubsidiary,
    DivPlaceholder,
    GetStartedButton,
    HeaderWrapper,
    LoginActionsWrapper,
    LoginLink,
    MobileCurrentSubsidiary,
    MobileLogoWrapper,
    SubsidiaryAndLogoWrapper,
} from './Header.styled';

export interface DefaultHeaderProps {
    className?: string;
    currentSubsidiary?: string;
    withLoginActions?: boolean;
}

export const DefaultHeader = ({
    className,
    currentSubsidiary,
    withLoginActions,
}: DefaultHeaderProps) => {
    const { t } = useTranslation();
    const isLg = useResponsive('lg');
    const isMd = useResponsive('md');
    const isSm = useResponsive('sm');
    const { pathname } = useLocation();
    const isHomepage = pathname === '/';

    const isLongSubsidiaryName = (currentSubsidiary?.length ?? 0) > 40;
    const shortenSubsidiaryName = `${currentSubsidiary?.slice(0, 40)}... `;

    if (!isMd)
        return (
            <HeaderWrapper className={className}>
                <SubsidiaryAndLogoWrapper>
                    <HeaderLogos />
                    {currentSubsidiary && <Divider />}
                    {isLongSubsidiaryName ? (
                        <Tooltip
                            placement={'bottom'}
                            removePortal
                            strategy={'fixed'}
                            testId="wgYSfsC"
                            text={currentSubsidiary}
                            width={320}
                        >
                            <div>
                                <CurrentSubsidiary>
                                    {shortenSubsidiaryName}
                                </CurrentSubsidiary>
                            </div>
                        </Tooltip>
                    ) : (
                        <CurrentSubsidiary>
                            {currentSubsidiary}
                        </CurrentSubsidiary>
                    )}
                </SubsidiaryAndLogoWrapper>

                {withLoginActions && !isSm ? (
                    <LoginActionsWrapper>
                        {isHomepage && <LanguageSelect />}
                        <LoginText hideQuestion={isLg} />
                        <LoginLink to={getPath({ path: '/auth/login/guest' })}>
                            <GetStartedButton
                                size="small"
                                testId="button_get-started"
                                variant="primary"
                            >
                                {t('GET_STARTED')}
                            </GetStartedButton>
                        </LoginLink>
                    </LoginActionsWrapper>
                ) : (
                    <LanguageSelect />
                )}
            </HeaderWrapper>
        );

    return (
        <HeaderWrapper className={className}>
            <DivPlaceholder />
            <MobileLogoWrapper>
                <HeaderLogos />
                <MobileCurrentSubsidiary>
                    {isLongSubsidiaryName
                        ? shortenSubsidiaryName
                        : currentSubsidiary}
                </MobileCurrentSubsidiary>
            </MobileLogoWrapper>

            <LanguageSelect />
        </HeaderWrapper>
    );
};
